/**
 * ProShip Direct v2 — one coverage file per account: pickup pincode, delivery pincode.
 *
 * The same two columns are read two different ways, because coverage genuinely means two
 * different things depending on what the account sells:
 *
 *   ROUTE MODE — Same Day, Next Day. Both cells required on every row, and the pickup-delivery
 *   pair is the unit. A carrier committing to 110001 → 110020 by tonight has said nothing about
 *   110001 → 560001 by tonight, so nothing is inferred between rows and the file must list every
 *   route it covers.
 *
 *   LIST MODE — Surface, Air, Express, Heavy, Reverse. Two independent lists: column one is the
 *   pickup pincodes, column two the delivery pincodes, and every pickup is taken to reach every
 *   delivery pincode. Rows may fill one cell or both, because the two lists are almost never the
 *   same length — 200 pickup locations against 19,000 delivery pincodes is normal, and requiring
 *   both cells would mean either 19,000 repetitions of the same pickup or 3.8 million rows.
 *
 * Asking a same-day contract for the cross-product would promise routes nobody sold. Asking a
 * surface contract to list them in pairs would be absurd. Hence one file, two readings, and the
 * screen states which one applies before the merchant uploads.
 *
 * Route-mode validation is not re-written here: `dryRunPdPairs` in serviceabilityUpload.js already
 * reads exactly that shape and reports rejected rows with reasons. List mode needs its own walk,
 * because dryRunPdPairs rejects a row with an empty cell and that is legal here — but it reuses the
 * same exported PINCODE_RE and `reason`, so the two branches cannot disagree about what a valid
 * pincode is or how to describe a bad one.
 */
import { dryRunPdPairs, PINCODE_RE, reason } from './serviceabilityUpload';

/**
 * The second column is delivery_pincode.
 *
 * The copied validator's own template says `drop_pincode`, but "delivery" is the word used
 * everywhere else a merchant meets this — on the order, on the label, on the tracking page — and a
 * screen that switches vocabulary makes the reader check whether it also switched meaning. The
 * validator does not care: it identifies the header by `/pickup/i` on the first line and reads the
 * second column positionally, so renaming it is safe.
 */
export const LANE_CSV_COLUMNS = ['pickup_pincode', 'delivery_pincode'];

/** Lane mode: every row is one pickup-to-delivery route. */
export const LANE_CSV_TEMPLATE = [
  LANE_CSV_COLUMNS.join(','),
  '110001,110020',
  '110001,201301',
  '560001,560095',
].join('\n');

/** List mode: the two columns run independently, so the example shows uneven lists. */
export const LIST_CSV_TEMPLATE = [
  LANE_CSV_COLUMNS.join(','),
  '110001,110020',
  '400001,201301',
  ',560095',
  ',700016',
  ',452001',
].join('\n');

/**
 * How many rows this build keeps on the device.
 *
 * A surface contract's coverage runs to tens of thousands of rows and belongs in the backend;
 * there is no endpoint yet. The whole file is validated, and the screen says so when it holds
 * less than it checked.
 */
export const LANE_ROW_CAP = 5000;

/** Non-empty rows, header dropped, split into their two cells with their real line numbers. */
const cellsOf = (text) =>
  String(text)
    .split(/\r?\n/)
    .map((raw, i) => ({ line: i + 1, raw }))
    .filter((row, i) => !(i === 0 && /pickup/i.test(row.raw)) && String(row.raw).trim())
    .map((row) => {
      const [pickup, delivery] = String(row.raw).split(',').map((c) => String(c || '').trim());
      return { ...row, pickup, delivery };
    });

/** Route mode: accepted, de-duplicated pickup-delivery pairs, in file order. */
const laneRows = (text) => {
  const seen = new Set();
  const rows = [];

  cellsOf(text).forEach(({ pickup, delivery }) => {
    if (!PINCODE_RE.test(pickup) || !PINCODE_RE.test(delivery)) return;
    const key = `${pickup}|${delivery}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ pickupPincode: pickup, deliveryPincode: delivery });
  });

  return rows;
};

/**
 * List mode: each cell joins its own list.
 *
 * A row contributes whatever cells it has. Empty is fine on either side; a non-empty cell that is
 * not a pincode is an error, named with the same `reason` lane mode uses.
 */
const listRows = (text) => {
  const pickups = new Set();
  const deliveries = new Set();
  const invalidDetails = [];

  cellsOf(text).forEach(({ line, pickup, delivery }) => {
    if (!pickup && !delivery) return;

    [
      ['Pickup', pickup, pickups],
      ['Delivery', delivery, deliveries],
    ].forEach(([which, value, target]) => {
      if (!value) return; // the other column carries this row
      if (!PINCODE_RE.test(value)) {
        if (invalidDetails.length < 50) {
          invalidDetails.push({ row: line, value, reason: `${which}: ${reason(value)}` });
        }
        return;
      }
      target.add(value);
    });
  });

  // Written back as two columns padded to the longer list, so download → edit → upload works.
  const pickupList = [...pickups];
  const deliveryList = [...deliveries];
  const rows = Array.from({ length: Math.max(pickupList.length, deliveryList.length) }, (ignored, i) => ({
    pickupPincode: pickupList[i] || '',
    deliveryPincode: deliveryList[i] || '',
  }));

  return { rows, pickupCount: pickups.size, deliveryCount: deliveries.size, invalidDetails };
};

/**
 * Validates a coverage file the way this account's service type requires.
 *
 * Returns one shape for both modes:
 *   laneWise       which reading was applied
 *   rows           what will be stored, and what a download writes back
 *   pickupCount    distinct pickup pincodes
 *   deliveryCount  distinct delivery pincodes
 *   lanes          routes explicitly listed (route mode; 0 in list mode)
 *   impliedLanes   pickupCount × deliveryCount (list mode; 0 in route mode)
 *   invalidDetails rejected rows with line numbers, capped at 50
 *   truncated      rows checked but not kept on the device
 */
export function parseCoverageFile(text, laneWise) {
  if (laneWise) {
    const dry = dryRunPdPairs(text);
    const rows = laneRows(text);

    // dryRunPdPairs numbers rejected rows as if line 1 were always a header. A file saved without
    // one then reports every error one line too high, which sends a merchant to the wrong row.
    // Corrected here rather than in the copied validator, so the copy stays a copy.
    const hasHeader = /pickup/i.test(String(text).split(/\r?\n/)[0] || '');
    const shifted = hasHeader
      ? dry.invalidDetails
      : dry.invalidDetails.map((row) => ({ ...row, row: row.row - 1 }));

    // The copied validator words its own rejections "Drop: ...". Remapped here so the screen reads
    // Delivery throughout, without editing the copy and starting a drift.
    const invalidDetails = shifted.map((row) => ({
      ...row,
      reason: String(row.reason || '').replace(/^Drop:/, 'Delivery:'),
    }));

    return {
      laneWise: true,
      rows,
      pickupCount: dry.pickupCount,
      deliveryCount: new Set(rows.map((r) => r.deliveryPincode)).size,
      lanes: rows.length,
      impliedLanes: 0,
      invalidDetails,
      truncated: Math.max(0, rows.length - LANE_ROW_CAP),
    };
  }

  const list = listRows(text);
  return {
    laneWise: false,
    rows: list.rows,
    pickupCount: list.pickupCount,
    deliveryCount: list.deliveryCount,
    lanes: 0,
    impliedLanes: list.pickupCount * list.deliveryCount,
    invalidDetails: list.invalidDetails,
    truncated: Math.max(0, list.rows.length - LANE_ROW_CAP),
  };
}

/** Current coverage back out as CSV, so download → edit → upload is lossless. */
export function coverageRowsToCsv(rows) {
  return [
    LANE_CSV_COLUMNS.join(','),
    ...(rows || []).map((r) => [r.pickupPincode || '', r.deliveryPincode || ''].join(',')),
  ].join('\n');
}
